import { useMemo } from "react";
import type { GridFeePreset } from "../data/gridFees";
import { GRID_FEE_PRESETS, GENERIC_SE } from "../data/gridFees";

interface GridOperatorSelectorProps {
  selectedId: string;
  onChange: (preset: GridFeePreset) => void;
}

/**
 * Två-stegs nätbolagsväljare.
 *
 * Steg 1: välj operator (Vattenfall, Ellevio, E.on, Annat).
 * Steg 2: när operator har flera varianter, visa abonnemang
 *         (16A, 20A, 25A, eventuella tariff-varianter).
 *
 * Skalar bättre när vi lägger till fler nätbolag. Default är generic
 * ("Annat / okänt nätbolag") så vi inte gissar fel auto-baserat.
 */
export default function GridOperatorSelector({
  selectedId,
  onChange,
}: GridOperatorSelectorProps) {
  const operators = useMemo(() => {
    const seen = new Set<string>();
    const list: string[] = [];
    for (const p of GRID_FEE_PRESETS) {
      if (!seen.has(p.operator)) {
        seen.add(p.operator);
        list.push(p.operator);
      }
    }
    return list;
  }, []);

  const selected =
    GRID_FEE_PRESETS.find((p) => p.id === selectedId) ?? GENERIC_SE;

  const subscriptionsForOperator = useMemo(
    () => GRID_FEE_PRESETS.filter((p) => p.operator === selected.operator),
    [selected.operator],
  );

  function handleOperatorChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const newOperator = e.target.value;
    const firstForOp = GRID_FEE_PRESETS.find((p) => p.operator === newOperator);
    if (firstForOp) onChange(firstForOp);
  }

  function handleSubscriptionChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const preset = GRID_FEE_PRESETS.find((p) => p.id === e.target.value);
    if (preset) onChange(preset);
  }

  return (
    <div className="grid-operator-selector">
      <div className="grid-operator-row">
        <label htmlFor="grid-operator-step1" className="grid-operator-label">
          Nätbolag
        </label>
        <select
          id="grid-operator-step1"
          className="grid-operator-select"
          value={selected.operator}
          onChange={handleOperatorChange}
        >
          {operators.map((op) => (
            <option key={op} value={op}>
              {op}
            </option>
          ))}
        </select>
      </div>

      {subscriptionsForOperator.length > 1 && (
        <div className="grid-operator-row">
          <label htmlFor="grid-operator-step2" className="grid-operator-label">
            Abonnemang / säkring
          </label>
          <select
            id="grid-operator-step2"
            className="grid-operator-select"
            value={selected.id}
            onChange={handleSubscriptionChange}
          >
            {subscriptionsForOperator.map((p) => (
              <option key={p.id} value={p.id}>
                {p.fuseSize}A · {p.description}
              </option>
            ))}
          </select>
        </div>
      )}

      <p className="grid-operator-help">
        Saknas ditt nätbolag eller stämmer inte priserna?{" "}
        <a
          href="mailto:hej@batterikalkyl.se?subject=Tips%20om%20n%C3%A4tbolag&body=Mitt%20n%C3%A4tbolag%3A%20%0AOmr%C3%A5de%3A%20%0AL%C3%A4nk%20till%20prislista%3A%20%0A"
          className="link"
        >
          Tipsa oss
        </a>{" "}
        så lägger vi till. Tills vidare ger "Annat / okänt nätbolag" ett
        riksgenomsnitt.
        {selected.tariff.sourceUrl && (
          <>
            {" "}
            <a
              href={selected.tariff.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="link"
            >
              Verifiera tariff
            </a>
            .
          </>
        )}
      </p>
    </div>
  );
}
